/**
 * Canonical classification of a DOM identifier's stability for use as a locator
 * anchor. Single source of truth — replaces the divergent, disagreeing
 * volatile/generated-id checks that used to live in (and drift between):
 *   - apps/api/src/workflows/cache/build-locator.ts (`findVolatileTestIdToken`)
 *   - packages/browser-core/src/snapshot-formatter-sections.ts (duplicate)
 *   - packages/recording/src/scope.ts (`looksGeneratedId`)
 *   - apps/api/src/workflows/cache/deterministic-locator-builder.ts (`isStableId`)
 *
 * Stability means **survives a re-seed**, NOT "points at a real record". The
 * canary app is re-seeded between runs, so any value derived from seeded data —
 * UUIDs, ULIDs, entity numbers (`invoice-DI-80368`), the `<timestamp>` in
 * `__new__1778798465222` — is VOLATILE even when it reliably identifies a record
 * within a single run. Only structural / framework-state tokens survive: a state
 * marker like `__new__`, a hard-coded structural `data-testid`, a landmark role.
 *
 * Output:
 *   - `stable`       → use the value verbatim (`[attr="value"]` / `#id`).
 *   - `stable-prefix`→ a structural head precedes a volatile token; anchor on
 *                      `[attr^="prefix"]` (a non-unique prefix is then rejected
 *                      by the caller's global-uniqueness check).
 *   - `volatile`     → unusable as an anchor.
 *
 * This is locator-time classification (a context-dependent judgment), kept out
 * of the substrate per substrate-v2 principle #3 ("synthesis is tagged, never
 * laundered").
 */
export type IdentifierStability =
  | { kind: 'stable' }
  | { kind: 'stable-prefix'; prefix: string }
  | { kind: 'volatile' };

// ── Whole-value generated patterns (no reusable structural prefix) ──────────
// Framework-generated id prefixes (React useId, Radix, HeadlessUI, Mantine,
// React Aria / Select, rc-, Vue menu-v-N).
const FRAMEWORK_PREFIX_RE =
  /^(?::r[0-9a-z]+:?$|_r_[0-9a-z]+_$|radix-|headlessui-|mantine-|react-aria-|react-select-|rc-|menu-v-\d)/i;
// A bare hash (styled-components / content hash), e.g. `deadbeef`, `a1b2c3d4e5`.
const BARE_HASH_RE = /^[a-f0-9]{8,}$/i;
// `prefix-<hex6+>` styled-component-style class/id, e.g. `sc-bdVaJa` → `foo-a1b2c3d4`.
const PREFIX_HASH_RE = /^[a-z]+-[0-9a-f]{6,}$/i;

// ── Embedded volatile tokens (a structural head may precede them) ───────────
// Any 8-4-4-4-12 hex value is a UUID regardless of version/variant nibble — keep
// it version-agnostic so v7/v8 (and non-RFC) ids are caught, not just v1-5.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
// ULID (Crockford base32, excludes I/L/O/U). Case-insensitive so lowercase
// ULIDs are caught too.
const ULID_RE = /\b[0-9A-HJKMNP-TV-Z]{26}\b/i;
// A 6+-digit run delimited by a boundary or `_` (so `__new__1778798465222`
// is caught even though `_`→digit is not a `\b`). Trailing/leading boundary
// keeps `abc123456def` from matching (preserves the legacy `\b\d{6,}\b`).
const DIGIT_RUN_RE = /(?:^|[^0-9A-Za-z])(\d{6,})(?![0-9A-Za-z])/;
// A trailing `-`/`_`-joined 3+-digit counter/number (`widget-123`,
// `invoice-DI-80368`, the timestamp tail of `__new__<ts>`). 1–2 trailing digits
// stay stable (`step-1`, `tab-42`).
const TRAILING_COUNTER_RE = /[-_](\d{3,})$/;

const MIN_PREFIX_LEN = 3;

function usablePrefix(prefix: string): boolean {
  return prefix.length >= MIN_PREFIX_LEN && /[a-z]/i.test(prefix);
}

/** Earliest index at which a volatile token begins, or null. */
function earliestVolatileTokenIndex(value: string): number | null {
  let best: number | null = null;
  const consider = (idx: number | undefined) => {
    if (idx !== undefined && idx >= 0 && (best === null || idx < best)) best = idx;
  };
  consider(UUID_RE.exec(value)?.index);
  consider(ULID_RE.exec(value)?.index);
  const digit = DIGIT_RUN_RE.exec(value);
  if (digit) consider(digit.index + (digit[0].length - digit[1].length)); // start of the digits
  const counter = TRAILING_COUNTER_RE.exec(value);
  if (counter) consider(counter.index + 1); // after the `-`/`_` separator
  return best;
}

export function classifyIdentifier(value: string): IdentifierStability {
  if (!value) return { kind: 'volatile' };

  // Whole-value generated → no reusable prefix.
  if (FRAMEWORK_PREFIX_RE.test(value) || BARE_HASH_RE.test(value) || PREFIX_HASH_RE.test(value)) {
    return { kind: 'volatile' };
  }

  const tokenIdx = earliestVolatileTokenIndex(value);
  if (tokenIdx !== null) {
    const prefix = value.slice(0, tokenIdx);
    return usablePrefix(prefix) ? { kind: 'stable-prefix', prefix } : { kind: 'volatile' };
  }

  return { kind: 'stable' };
}

/** True when the value is not safe to anchor on verbatim (volatile or prefix-only). */
export function isVolatileIdentifier(value: string): boolean {
  return classifyIdentifier(value).kind !== 'stable';
}

/** The structural prefix preceding a volatile token, or null. */
export function stableIdentifierPrefix(value: string): string | null {
  const c = classifyIdentifier(value);
  return c.kind === 'stable-prefix' ? c.prefix : null;
}

function escapeCssAttrValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * CSS attribute selector for `value` on `attr` (`[attr="value"]` for a stable
 * value, `[attr^="prefix"]` for a structural prefix), or null when too volatile.
 */
export function stableAttrSelector(attr: string, value: string): string | null {
  const c = classifyIdentifier(value);
  if (c.kind === 'stable') return `[${attr}="${escapeCssAttrValue(value)}"]`;
  if (c.kind === 'stable-prefix') return `[${attr}^="${escapeCssAttrValue(c.prefix)}"]`;
  return null;
}
